<script>
    import onboardingService from '@/services/onboarding.service';
    import companiesService from '@/services/companies.service';
    import Spinner from '@/components/shared/Spinner';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import CompanySearchSelect from '@/components/shared/CompanySearchSelect';

    /** Gear Up opens AutoOnboarding (9) - company setup, not BatteryImport. */
    const AUTO_ONBOARDING_JOB_KIND = 9;

    export default {
        components: { Spinner, ButtonSpinner, CompanySearchSelect },
        data() {
            return {
                detail: null,
                isLoading: true,
                error: null,
                isSaving: false,
                isNewCompany: false,
                selectedCompany: null,
                newCompanyName: '',
                newUserFullName: '',
                newUserUsername: '',
                newUserEmail: ''
            };
        },
        computed: {
            submissionId() {
                return parseInt(this.$route.params.submissionId, 10);
            },
            alreadyLinked() {
                return !!this.detail?.sessionId;
            },
            summaryLine() {
                if (!this.detail) return '';
                const lease = this.detail.hasLeaseFile ? 'lease file on Typeform' : 'no lease file';
                return `${this.detail.transportCount} transport · ${this.detail.pumperCount} pumpers · ${this.detail.downtimeCount} downtime reasons · ${lease}`;
            }
        },
        methods: {
            apiError(err, fallback) {
                return err?.data?.message || err?.message || fallback;
            },
            prefillFromDetail(d) {
                this.newCompanyName = d.companyName || '';
                this.newUserFullName = d.contactName || '';
                this.newUserEmail = d.contactEmail || '';
                this.newUserUsername = d.contactEmail || '';
                const match = (d.matchingCompanies || [])[0];
                if (match) {
                    this.isNewCompany = false;
                    this.selectedCompany = { id: String(match.id), name: match.name };
                } else {
                    this.isNewCompany = true;
                    this.selectedCompany = null;
                }
            },
            pickMatch(match) {
                this.isNewCompany = false;
                this.selectedCompany = { id: String(match.id), name: match.name };
            },
            async load() {
                this.isLoading = true;
                this.error = null;
                try {
                    this.detail = await onboardingService.getGearUp(this.submissionId);
                    this.prefillFromDetail(this.detail);
                } catch (err) {
                    this.error = this.apiError(err, 'Could not load this Gear Up submission.');
                    this.detail = null;
                } finally {
                    this.isLoading = false;
                }
            },
            async resolveCompanyId() {
                if (!this.isNewCompany) {
                    return parseInt(this.selectedCompany?.id, 10) || null;
                }

                const created = await companiesService.createLean({
                    name: this.newCompanyName,
                    fullName: this.newUserFullName,
                    username: this.newUserUsername,
                    email: this.newUserEmail
                });
                return parseInt(created?.id, 10) || null;
            },
            async process() {
                if (this.alreadyLinked) {
                    this.$router.push(`/onboarding/${this.detail.sessionId}`);
                    return;
                }

                if (this.isNewCompany && !this.newCompanyName.trim()) {
                    this.error = 'Company name is required.';
                    return;
                }
                if (this.isNewCompany && !this.newUserUsername.trim()) {
                    this.error = 'A first user is required — the company needs an account to sign in with.';
                    return;
                }
                if (!this.isNewCompany && !parseInt(this.selectedCompany?.id, 10)) {
                    this.error = 'Pick an existing company, or create one.';
                    return;
                }

                this.isSaving = true;
                this.error = null;
                try {
                    const companyId = await this.resolveCompanyId();
                    if (!companyId) {
                        this.error = 'Could not resolve the company.';
                        return;
                    }

                    const session = await onboardingService.processGearUp(this.submissionId, {
                        companyId,
                        targetJobKind: AUTO_ONBOARDING_JOB_KIND
                    });
                    if (session?.id) this.$router.push(`/onboarding/${session.id}`);
                    else this.$router.push('/onboarding');
                } catch (err) {
                    this.error = this.apiError(err, 'Could not open the onboarding session.');
                } finally {
                    this.isSaving = false;
                }
            }
        },
        watch: {
            submissionId: {
                immediate: true,
                handler(id) {
                    if (id > 0) this.load();
                }
            }
        }
    };
</script>

<template>
    <div class="container-fluid py-3">
        <div class="mb-3">
            <router-link class="small" to="/onboarding">← Gear Up</router-link>
        </div>

        <Spinner v-if="isLoading" />

        <div v-else-if="!detail" class="alert alert-danger">{{ error || 'Submission not found.' }}</div>

        <template v-else>
            <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                <div>
                    <h1 class="h3 mb-1">{{ detail.companyName || 'Gear Up submission' }}</h1>
                    <p class="text-muted mb-0">
                        Confirm what Typeform sent, pick the company, then open the onboarding session.
                    </p>
                </div>
                <span class="badge text-bg-warning align-self-start" v-if="!alreadyLinked">Pending</span>
                <span class="badge text-bg-success align-self-start" v-else>Session linked</span>
            </div>

            <div v-if="error" class="alert alert-danger">{{ error }}</div>

            <div class="border rounded p-3 mb-4 bg-light">
                <div class="fw-semibold mb-1">ONBOARDING REVIEW</div>
                <div class="small text-muted mb-2">
                    Submitted {{ detail.submittedAt || 'unknown' }}
                    <span v-if="detail.responseId"> · Response {{ detail.responseId }}</span>
                </div>
                <div>{{ summaryLine }}</div>
                <div class="small text-muted mt-2">
                    Facilities, wells, tanks and meters are not in this form; they come from the lease file.
                </div>
            </div>

            <div class="row g-4 mb-4">
                <div class="col-lg-6">
                    <h2 class="h5">From the form</h2>
                    <dl class="row mb-0 gear-up-dl">
                        <dt class="col-sm-4">Bookie</dt>
                        <dd class="col-sm-8">
                            <div>{{ detail.contactName || '—' }}</div>
                            <div class="small text-muted">{{ detail.contactEmail }}</div>
                        </dd>
                        <dt class="col-sm-4">Second contact</dt>
                        <dd class="col-sm-8">
                            <div>{{ detail.secondaryContactName || '—' }}</div>
                            <div class="small text-muted">{{ detail.secondaryContactEmail }}</div>
                        </dd>
                        <dt class="col-sm-4">Submitter</dt>
                        <dd class="col-sm-8">{{ detail.submitterEmail || '—' }}</dd>
                        <dt class="col-sm-4">Phone</dt>
                        <dd class="col-sm-8">{{ detail.companyPhone || '—' }}</dd>
                        <dt class="col-sm-4">Purchasers</dt>
                        <dd class="col-sm-8 text-break">{{ detail.purchasers || '—' }}</dd>
                        <dt class="col-sm-4">Haulers</dt>
                        <dd class="col-sm-8 text-break">{{ detail.haulers || '—' }}</dd>
                        <dt class="col-sm-4">Downtime</dt>
                        <dd class="col-sm-8"><pre class="gear-up-pre mb-0">{{ detail.downtimeReasons || '—' }}</pre></dd>
                        <dt class="col-sm-4">Pumpers</dt>
                        <dd class="col-sm-8"><pre class="gear-up-pre mb-0">{{ detail.pumperInfo || '—' }}</pre></dd>
                        <dt class="col-sm-4">Lease file</dt>
                        <dd class="col-sm-8">
                            <template v-if="detail.hasLeaseFile">
                                <div>On Typeform</div>
                                <a
                                    v-if="detail.leaseFileUrl"
                                    class="small"
                                    :href="detail.leaseFileUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >Open Typeform file</a>
                            </template>
                            <span v-else class="text-muted">None on this response</span>
                        </dd>
                    </dl>
                </div>

                <div class="col-lg-6">
                    <h2 class="h5">Open onboarding</h2>
                    <p v-if="alreadyLinked" class="text-muted">
                        This submission is already linked to session {{ detail.sessionId }}.
                    </p>
                    <form v-else class="gsb-form" @submit.prevent="process">
                        <div v-if="detail.matchingCompanies?.length" class="mb-3">
                            <div class="small text-muted mb-1">Name matches in GreaseBook</div>
                            <div class="d-flex flex-wrap gap-2">
                                <button
                                    v-for="m in detail.matchingCompanies"
                                    :key="m.id"
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    @click="pickMatch(m)"
                                >
                                    {{ m.name }}
                                    <span class="opacity-75">#{{ m.id }}</span>
                                </button>
                            </div>
                        </div>

                        <div class="mb-3">
                            <div class="form-check form-check-inline">
                                <input
                                    id="gearUpCompanyExisting"
                                    v-model="isNewCompany"
                                    class="form-check-input"
                                    type="radio"
                                    :value="false"
                                />
                                <label class="form-check-label" for="gearUpCompanyExisting">Existing company</label>
                            </div>
                            <div class="form-check form-check-inline">
                                <input
                                    id="gearUpCompanyNew"
                                    v-model="isNewCompany"
                                    class="form-check-input"
                                    type="radio"
                                    :value="true"
                                />
                                <label class="form-check-label" for="gearUpCompanyNew">Create company</label>
                            </div>
                        </div>

                        <div v-if="!isNewCompany" class="mb-3">
                            <label class="form-label" for="gearUpCompanySearch">Company</label>
                            <CompanySearchSelect
                                v-model="selectedCompany"
                                input-id="gearUpCompanySearch"
                                :placeholder="detail.companyName || 'Search companies'"
                            />
                        </div>
                        <template v-else>
                            <div class="mb-3">
                                <label class="form-label" for="gearUpNewName">Company name</label>
                                <input id="gearUpNewName" v-model="newCompanyName" class="form-control" type="text" />
                            </div>
                            <div class="mb-3">
                                <label class="form-label" for="gearUpFullName">First user full name</label>
                                <input id="gearUpFullName" v-model="newUserFullName" class="form-control" type="text" />
                            </div>
                            <div class="mb-3">
                                <label class="form-label" for="gearUpUsername">Username</label>
                                <input id="gearUpUsername" v-model="newUserUsername" class="form-control" type="text" />
                            </div>
                            <div class="mb-3">
                                <label class="form-label" for="gearUpEmail">Email</label>
                                <input id="gearUpEmail" v-model="newUserEmail" class="form-control" type="email" />
                            </div>
                        </template>

                        <p class="small text-muted mb-3">
                            Opens an <strong>AutoOnboarding</strong> session - users, transport,
                            pumpers and lease setup - not a BatteryImport of one facility.
                        </p>

                        <button class="btn btn-primary" type="submit" :disabled="isSaving">
                            <ButtonSpinner v-if="isSaving" />
                            {{ isSaving ? 'Opening…' : 'Confirm and open session' }}
                        </button>
                    </form>

                    <button
                        v-if="alreadyLinked"
                        class="btn btn-primary"
                        type="button"
                        @click="process"
                    >
                        Open session {{ detail.sessionId }}
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.gear-up-dl dt {
    font-weight: 600;
}
.gear-up-pre {
    white-space: pre-wrap;
    font-family: inherit;
    font-size: inherit;
}
</style>